<template>
  <!-- #271 · El interruptor del camino manual de vacaciones, que acciona Dirección a mano: sin
       fecha, reversible y con apunte de quién y cuándo. Vive en esta pantalla porque es de la misma
       gente —ADMIN y DIRECTOR— y del mismo tema que las normas, y así no hace falta otra entrada de
       menú. Va al final y con su propio título: no cambia nada de lo que la pantalla ya tenía. -->
  <div class="interruptor-camino-manual">
    <f7-block-title>{{ $t('caminoManual.titulo') }}</f7-block-title>
    <f7-block strong class="no-margin-top">
      <p class="explicacion">{{ $t('caminoManual.explicacion') }}</p>

      <!-- Mientras no ha contestado el servidor no se dice ninguna posición: enseñar «Permitido»
           de partida sería afirmar lo que no se sabe. -->
      <p v-if="cargando && !estado" class="posicion">{{ $t('caminoManual.cargando') }}</p>
      <!-- Un fallo al recargar manda aunque haya una posición de antes: la ruta es `keepAlive` y
           esa posición puede ser de hace horas. Enseñarla con el botón encendido sería dejar
           accionar sobre algo que no se ha podido comprobar. -->
      <p v-else-if="errorCarga" class="posicion aviso">
        {{ $t('caminoManual.errorCarga') }}
        <f7-link class="margin-left-half" @click="cargar()">{{ $t('caminoManual.reintentar') }}</f7-link>
      </p>
      <template v-else-if="estado">
        <p class="posicion">
          <span class="etiqueta" :class="estado.apagado ? 'etiqueta-apagado' : 'etiqueta-permitido'">
            {{ estado.apagado ? $t('caminoManual.apagado') : $t('caminoManual.permitido') }}
          </span>
          <span class="que-supone">
            {{ estado.apagado ? $t('caminoManual.explicacionApagado') : $t('caminoManual.explicacionPermitido') }}
          </span>
        </p>
        <!-- La posición que manda no la ha dejado ningún cambio de la lista: se dice junto a la
             lista, que es lo que contradiría. -->
        <p v-if="!estado.coincideConElUltimoApunte" class="aviso">{{ $t('caminoManual.noCoincide') }}</p>
        <!-- Lo que dice el servidor al contestar un cambio que sí se guardó: por ejemplo, que no ha
             podido releer la lista y solo trae el cambio de ahora. -->
        <p v-for="(a, i) in (estado.avisos || [])" :key="'aviso-' + i" class="aviso">{{ a }}</p>
        <f7-button
          fill
          :color="estado.apagado ? 'blue' : 'red'"
          :disabled="cambiando || cargando"
          @click="accionar(!estado.apagado)"
        >
          {{ estado.apagado ? $t('caminoManual.permitir') : $t('caminoManual.apagar') }}
        </f7-button>
        <!-- §9: el botón apagado dice por qué. Se ve sobre todo al volver a la pantalla con el
             cambio todavía en vuelo, que es cuando nadie recuerda haberlo pulsado. -->
        <p v-if="cambiando" class="explicacion margin-top-half">{{ $t('caminoManual.enVuelo') }}</p>
      </template>
    </f7-block>

    <template v-if="estado && !errorCarga">
      <f7-block-title>{{ $t('caminoManual.historial') }}</f7-block-title>
      <div class="tabla-cambios">
        <div v-for="(c, i) in estado.cambios" :key="i" class="fila">
          <span class="col-fecha">{{ c.fecha | dateFormat('DD/MM/YYYY HH:mm') }}</span>
          <span class="col-quien">{{ c.realizadoPor }}</span>
          <span class="col-que">{{ c.apagado ? $t('caminoManual.cambioApagado') : $t('caminoManual.cambioPermitido') }}</span>
        </div>
        <div v-if="!estado.cambios || !estado.cambios.length" class="fila vacia">
          {{ $t('caminoManual.sinCambios') }}
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.explicacion { font-size: 13px; color: #475569; margin: 0 0 10px; }
.posicion { font-size: 13px; margin: 0 0 10px; display: flex; align-items: baseline; flex-wrap: wrap; }
.etiqueta {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: bold;
  color: #fff;
  margin-right: 8px;
}
.etiqueta-permitido { background-color: #22355b; }
.etiqueta-apagado { background-color: #b91c1c; }
.que-supone { flex: 1 1 260px; color: #334155; }
.aviso { font-size: 13px; color: #92400e; background-color: #fef3c7; padding: 6px 8px; }
.tabla-cambios { font-size: 13px; padding: 0 10px; }
.tabla-cambios .fila { display: flex; align-items: baseline; padding: 6px 8px; border-bottom: 1px solid #f0f0f0; }
.tabla-cambios .vacia { color: #8e8e93; }
.col-fecha { width: 140px; }
.col-quien { width: 140px; }
.col-que { flex: 1 1 auto; }
</style>

<script>
import RhVacaciones from "./../../js/db/RhVacaciones.js";
import accionDeLaSesion from "./../../js/utils/accionDeLaSesion.js";
import identidadSesion, { sigueLaSesion } from "./../../js/utils/identidadSesion.js";
import mensajeDelServidor from "./../../js/utils/mensajeDelServidor.js";
import soloMandaLaDeDelante from "./../../js/utils/soloMandaLaDeDelante.js";
import { unaAccionALaVez } from "./../../js/utils/unaAccionALaVez.js";

// El cerrojo de `accionDeLaSesion`: el interruptor es uno solo para toda la aplicación.
const CLAVE_CAMINO_MANUAL = 'vacaciones:camino-manual';

export default {
  // La tarjeta lleva su propio sello de pantalla y no el de «Normas de vacaciones», que no usa este
  // mixin: es lo que `accionDeLaSesion` mira para no aceptar la confirmación, ni pintar lo que
  // conteste el cambio, ni dejar puesta la espera global, cuando ya se ha salido de aquí. Sin él, el
  // sello no encontraba a nadie y respondía siempre que la pantalla seguía delante (revisión CODEX
  // del PR #1867, ronda 2). La salida se la avisa la pantalla por `soltar`.
  //
  // Y «hay un cambio en vuelo» lo dice el cerrojo del módulo (`unaAccionALaVez`), no un campo de
  // la tarjeta. Era un `accionando` propio que solo bajaba al contestar el servidor: al cambiar de
  // cuenta con el PUT colgado, la ruta `keepAlive` conservaba la tarjeta y el botón seguía apagado
  // para la cuenta nueva (revisión CODEX del PR #1867, ronda 4). El cerrojo es el que ya coge
  // `accionDeLaSesion` al enviar, lo tira el cambio de cuenta y no lo suelta salir de la pantalla:
  // una sola cuenta de lo que hay en vuelo, sin una copia aquí que se quede atrás.
  mixins: [soloMandaLaDeDelante, unaAccionALaVez],
  data: function() {
    return {
      // `{ apagado, coincideConElUltimoApunte, cambios[] }` tal como lo contestó el servidor.
      estado: null,
      cargando: false,
      errorCarga: false,
      // Sube con cada lectura y al salir de la pantalla: solo pinta la respuesta de la última. La
      // ruta tiene `keepAlive`, así que una lectura lenta de una visita anterior seguiría viva.
      pedida: 0
    };
  },
  computed: {
    /** Si el interruptor se está cambiando ahora mismo, desde esta tarjeta o desde otra. */
    cambiando: function() {
      return this.accionEnVueloSobre(CLAVE_CAMINO_MANUAL);
    }
  },
  methods: {
    /** Lo llama la pantalla al entrar: la posición puede haber cambiado desde la última visita. */
    cargar: function() {
      const self = this;
      const esta = ++self.pedida;
      // Y de qué sesión: lo que conteste con otra cuenta ya dentro no es lo que esa cuenta pidió.
      const deLaSesion = identidadSesion();
      self.cargando = true;
      self.errorCarga = false;
      return RhVacaciones.caminoManual().then(function(dto) {
        if (esta !== self.pedida || !sigueLaSesion(deLaSesion)) return;
        if (!dto || typeof dto.apagado != 'boolean') {
          throw { message: 'Respuesta inesperada de camino-manual' };
        }
        self.estado = dto;
      }).catch(function() {
        if (esta !== self.pedida || !sigueLaSesion(deLaSesion)) return;
        self.errorCarga = true;
      }).then(function() {
        if (esta === self.pedida) self.cargando = false;
      });
    },
    /** Lo llama la pantalla al salir (`@page:beforeout`): es la salida de esta tarjeta. */
    soltar: function() {
      this.salirDeLaPantalla();
    },
    /**
     * Las tres salidas de `soloMandaLaDeDelante` —salir de la pantalla, cerrar sesión, destruirla—
     * acaban aquí. Lo que llegue después no tiene dónde pintarse. La confirmación que siguiera
     * abierta no se cierra aquí: es de la acción, y la cancela `accionDeLaSesion` por las mismas
     * salidas (#271). El cambio que ya ha salido hacia el servidor no se puede retirar; lo que haya
     * dejado lo enseña la carga al volver a entrar.
     */
    dejarDeMandar: function() {
      this.pedida++;
      this.cargando = false;
    },
    /**
     * Pide confirmación y lo deja en la posición elegida. Se manda la posición, no «cámbialo»: si
     * otra persona lo ha movido entretanto, el servidor no apunta un cambio que no se ha pedido.
     *
     * La confirmación lleva dentro la sesión que la abrió (regla de la #59, `accionDeLaSesion`):
     * cerrar sesión la cierra, y aunque se llegara a aceptar con otra cuenta dentro no acciona nada
     * y lo dice. Sin eso, el cambio salía con el token de la cuenta nueva y el historial se lo
     * apuntaba a ella (revisión CODEX del PR #1867).
     */
    accionar: function(apagar) {
      const self = this;
      const accion = accionDeLaSesion(self, CLAVE_CAMINO_MANUAL);
      // `abrir` y no `confirmar`, que rotularía los botones en inglés; «Cancelar» primero, como el
      // resto de preguntas de la aplicación. La pregunta es de la acción y no de la tarjeta: una
      // segunda pulsación antes de que se abra no abre otra, y salir, cambiar de cuenta o
      // destruir la tarjeta la cierran sin que aquí se guarde nada (revisión CODEX del PR #1867,
      // ronda 5, y decisión técnica del 23/10/2026).
      accion.abrir({
        title: self.$t(apagar ? 'caminoManual.confirmarApagarTitulo' : 'caminoManual.confirmarPermitirTitulo'),
        text: self.$t(apagar ? 'caminoManual.confirmarApagar' : 'caminoManual.confirmarPermitir'),
        buttons: [
          { text: self.$t('caminoManual.cancelar'), keyCodes: [27] },
          { text: self.$t('caminoManual.confirmar'), bold: true, onClick: function() { self.enviar(apagar, accion); } }
        ]
      });
    },
    /**
     * El envío, ya sellado. Lo que conteste —pintar la posición nueva o avisar del error— solo se
     * hace si sigue la misma sesión: una respuesta tardía no se le enseña a la cuenta que haya
     * entrado después.
     *
     * El doble envío lo para `accionDeLaSesion`: la confirmación se acepta una sola vez, y con éste
     * en vuelo no se abre otra y se dice por qué (cerrojo de `CLAVE_CAMINO_MANUAL`).
     */
    enviar: function(apagar, accion) {
      const self = this;
      const esta = ++self.pedida;
      accion.enviar(RhVacaciones.accionarCaminoManual(apagar), {
        exito: function(dto) {
          if (esta !== self.pedida) return;
          if (dto && typeof dto.apagado == 'boolean') {
            self.estado = dto;
            self.errorCarga = false;
          } else {
            // Se ha guardado, pero la respuesta no se sabe leer: lo que haya quedado lo dice una
            // lectura, no lo que se pidió.
            self.cargar();
          }
        },
        fallo: function(e) {
          // Un fallo no previsto trae el texto de la excepción de Java: ese no se enseña (revisión
          // CODEX del PR #1867, ronda 1 de la entrega).
          self.$f7.dialog.alert(self.$t('caminoManual.errorAccionar', {
            motivo: mensajeDelServidor(e, self.$t('caminoManual.errorCarga'))
          }));
          // Lo que haya quedado lo dice el servidor, no lo que se pidió: un 409 o un corte de red
          // pueden haber dejado cualquiera de las dos posiciones.
          if (esta === self.pedida) self.cargar();
        }
      });
    }
  }
};
</script>
